/**
 * Shared option lists for settings select / multi-select controls.
 *
 * Enum option values mirror the OpenAPI contract enums exactly; labels are the
 * human-readable presentation. Locale / currency / timezone lists are a curated
 * starting set (the controls still accept whatever the server returns).
 *
 * NOTE: every `label` below (except `LANGUAGE_OPTIONS` and `TIMEZONE_OPTIONS`, see their
 * own comments) is an i18next KEY, not literal display text — consumers must render
 * `t(option.label)`. Translations live in `public/locales/{lng}/settings.json` under the
 * `options` key. This mirrors the pattern in `catalog/model/options.ts`.
 */
import type {
  AddressFormat,
  CartMergeStrategy,
  DeliveryModel,
  EngineMode,
  EvotorIntegrationStepKey,
  InventoryTrackingMode,
  NotificationChannel,
  OmsMode,
  OutOfStockBehavior,
  PaymentCaptureMode,
  PaymentMethod,
  PosifloraCapabilities,
  RegistrationMode,
  ReservationPolicy,
  SalesMode,
  StoreDayOfWeek,
  StoreLocationServiceType,
  StoreLocationType,
  StoreRegionType,
} from '../model/settingsTypes';

export interface Option<T extends string = string> {
  value: T;
  label: string;
}

// NOTE: labels here are deliberately literal (native endonyms), NOT i18n keys — this is a
// language-name picker, and each language names itself in its own script regardless of the
// active UI language (a Russian speaker recognises "Русский" whether the portal chrome
// around it is in English or Chinese). Not a translation gap — see i18n-compliance-audit.md
// "What isn't a bug".
export const LANGUAGE_OPTIONS: Option[] = [
  { value: 'ru-RU', label: 'Русский (ru-RU)' },
  { value: 'en-US', label: 'English (en-US)' },
  { value: 'en-GB', label: 'English (en-GB)' },
  { value: 'kk-KZ', label: 'Қазақша (kk-KZ)' },
  { value: 'uz-UZ', label: 'O‘zbek (uz-UZ)' },
];

/**
 * Bare locale codes for the Content domain's `defaultLocale` (web-content-service page
 * lookup). NOT the same vocabulary as `LANGUAGE_OPTIONS` (BCP-47 tags for
 * `BusinessInformation.locale`) — content pages can only be published with `locale ∈
 * {'ru', 'en'}` (mirrors `ContentPagesPage.tsx`'s `LOCALE_OPTIONS`). Picking a BCP-47
 * tag here would not match any published page's locale and would blank the storefront's
 * "About shop" content silently — do not reuse `LANGUAGE_OPTIONS` for this field.
 *
 * NOTE: `label` is an i18next KEY (e.g. 'settings:options.contentLocale.ru'), not literal
 * display text — render with `t(option.label)`.
 */
export const CONTENT_LOCALE_OPTIONS: Option[] = [
  { value: 'ru', label: 'settings:options.contentLocale.ru' },
  { value: 'en', label: 'settings:options.contentLocale.en' },
];

/** i18next keys — render with `t(option.label)`. */
export const CURRENCY_OPTIONS: Option[] = [
  { value: 'RUB', label: 'settings:options.currency.rub' },
  { value: 'USD', label: 'settings:options.currency.usd' },
  { value: 'EUR', label: 'settings:options.currency.eur' },
  { value: 'KZT', label: 'settings:options.currency.kzt' },
  { value: 'UZS', label: 'settings:options.currency.uzs' },
];

// NOTE: labels here are deliberately literal, NOT i18n keys — IANA timezone database
// identifiers (e.g. "Europe/Moscow") are technical identifiers, not UI prose; they're kept
// in their canonical English form across every locale, same treatment as `LANGUAGE_OPTIONS`.
export const TIMEZONE_OPTIONS: Option[] = [
  { value: 'Europe/Kaliningrad', label: 'Europe/Kaliningrad (UTC+2)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' },
  { value: 'Europe/Samara', label: 'Europe/Samara (UTC+4)' },
  { value: 'Asia/Yekaterinburg', label: 'Asia/Yekaterinburg (UTC+5)' },
  { value: 'Asia/Almaty', label: 'Asia/Almaty (UTC+6)' },
  { value: 'Asia/Krasnoyarsk', label: 'Asia/Krasnoyarsk (UTC+7)' },
  { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok (UTC+10)' },
];

/** i18next keys — render with `t(option.label)`. */
export const DELIVERY_MODEL_OPTIONS: Option<DeliveryModel>[] = [
  { value: 'COURIER', label: 'settings:options.deliveryModel.courier' },
  { value: 'PICKUP_FROM_STORE', label: 'settings:options.deliveryModel.pickupFromStore' },
  { value: 'PICKUP_POINT', label: 'settings:options.deliveryModel.pickupPoint' },
  { value: 'CLICK_AND_COLLECT', label: 'settings:options.deliveryModel.clickAndCollect' },
  { value: 'CLICK_AND_RESERVE', label: 'settings:options.deliveryModel.clickAndReserve' },
  { value: 'SAME_DAY', label: 'settings:options.deliveryModel.sameDay' },
];

/** i18next keys — render with `t(option.label)`. */
export const PAYMENT_METHOD_OPTIONS: Option<PaymentMethod>[] = [
  { value: 'CARD_ONLINE', label: 'settings:options.paymentMethod.cardOnline' },
  { value: 'CASH_ON_DELIVERY', label: 'settings:options.paymentMethod.cashOnDelivery' },
  { value: 'INSTALLMENTS', label: 'settings:options.paymentMethod.installments' },
  { value: 'CREDIT', label: 'settings:options.paymentMethod.credit' },
];

/** i18next keys — render with `t(option.label)`. */
export const PAYMENT_CAPTURE_MODE_OPTIONS: Option<PaymentCaptureMode>[] = [
  { value: 'ONE_STAGE', label: 'settings:options.paymentCaptureMode.oneStage' },
  { value: 'TWO_STAGE', label: 'settings:options.paymentCaptureMode.twoStage' },
];

/** i18next keys — render with `t(option.label)`. */
export const OMS_MODE_OPTIONS: Option<OmsMode>[] = [
  { value: 'INTERNAL', label: 'settings:options.omsMode.internal' },
  { value: 'EXTERNAL', label: 'settings:options.omsMode.external' },
];

/** i18next keys — render with `t(option.label)`. */
export const ENGINE_MODE_OPTIONS: Option<EngineMode>[] = [
  { value: 'INTERNAL', label: 'settings:options.engineMode.internal' },
  { value: 'EXTERNAL', label: 'settings:options.engineMode.external' },
];

/** i18next keys — render with `t(option.label)`. */
export const REGISTRATION_MODE_OPTIONS: Option<RegistrationMode>[] = [
  { value: 'OPEN', label: 'settings:options.registrationMode.open' },
  { value: 'INVITE_ONLY', label: 'settings:options.registrationMode.inviteOnly' },
  { value: 'DISABLED', label: 'settings:options.registrationMode.disabled' },
];

/** i18next keys — render with `t(option.label)`. */
export const NOTIFICATION_CHANNEL_OPTIONS: Option<NotificationChannel>[] = [
  { value: 'EMAIL', label: 'settings:options.notificationChannel.email' },
  { value: 'SMS', label: 'settings:options.notificationChannel.sms' },
  { value: 'PUSH', label: 'settings:options.notificationChannel.push' },
  { value: 'TELEGRAM', label: 'settings:options.notificationChannel.telegram' },
];

// ── Platform services added in docs/obzr-ecom-platform.md ────────────────────────

/** i18next keys — render with `t(option.label)`. */
export const SALES_MODE_OPTIONS: Option<SalesMode>[] = [
  { value: 'PRE_OPENING', label: 'settings:options.salesMode.preOpening' },
  { value: 'ACTIVE', label: 'settings:options.salesMode.active' },
  { value: 'PAUSED', label: 'settings:options.salesMode.paused' },
  { value: 'MAINTENANCE', label: 'settings:options.salesMode.maintenance' },
];

/** i18next keys — render with `t(option.label)`. */
export const STORE_LOCATION_TYPE_OPTIONS: Option<StoreLocationType>[] = [
  { value: 'RETAIL', label: 'settings:options.storeLocationType.retail' },
  { value: 'WAREHOUSE', label: 'settings:options.storeLocationType.warehouse' },
  { value: 'PICKUP_POINT', label: 'settings:options.storeLocationType.pickupPoint' },
  { value: 'DARK_STORE', label: 'settings:options.storeLocationType.darkStore' },
];

/** i18next keys — render with `t(option.label)`. */
export const STORE_SERVICE_LOCATION_TYPE_OPTIONS: Option<StoreLocationServiceType>[] = [
  { value: 'STORE', label: 'settings:options.storeServiceLocationType.store' },
  { value: 'WAREHOUSE', label: 'settings:options.storeServiceLocationType.warehouse' },
  { value: 'DARK_STORE', label: 'settings:options.storeServiceLocationType.darkStore' },
  { value: 'SHOWROOM', label: 'settings:options.storeServiceLocationType.showroom' },
  { value: 'PICKUP_POINT', label: 'settings:options.storeServiceLocationType.pickupPoint' },
];

/** i18next keys — render with `t(option.label)`. */
export const STORE_REGION_TYPE_OPTIONS: Option<StoreRegionType>[] = [
  { value: 'COUNTRY', label: 'settings:options.storeRegionType.country' },
  { value: 'REGION', label: 'settings:options.storeRegionType.region' },
  { value: 'CITY', label: 'settings:options.storeRegionType.city' },
];

/**
 * Monday-first day-of-week options for the store-service `StoreBusinessHours` editor
 * (`LocationDialog`'s business-hours grid). NOT the same constant as {@link WEEKDAYS}
 * below, which drives a different, legacy numeric-day `WorkingHours` type — do not
 * conflate the two data shapes. They DO intentionally share the same
 * `settings:options.weekday.*` i18n keys (below) since "Monday" names the same calendar
 * day in both contexts — no risk of the label meaning something different.
 *
 * i18next keys — render with `t(option.label)`.
 */
export const STORE_DAY_OF_WEEK_OPTIONS: Option<StoreDayOfWeek>[] = [
  { value: 'MONDAY', label: 'settings:options.weekday.monday' },
  { value: 'TUESDAY', label: 'settings:options.weekday.tuesday' },
  { value: 'WEDNESDAY', label: 'settings:options.weekday.wednesday' },
  { value: 'THURSDAY', label: 'settings:options.weekday.thursday' },
  { value: 'FRIDAY', label: 'settings:options.weekday.friday' },
  { value: 'SATURDAY', label: 'settings:options.weekday.saturday' },
  { value: 'SUNDAY', label: 'settings:options.weekday.sunday' },
];

/** i18next keys — render with `t(option.label)`. */
export const ADDRESS_FORMAT_OPTIONS: Option<AddressFormat>[] = [
  { value: 'STRUCTURED', label: 'settings:options.addressFormat.structured' },
  { value: 'FREEFORM', label: 'settings:options.addressFormat.freeform' },
];

/** i18next keys — render with `t(option.label)`. */
export const INVENTORY_TRACKING_MODE_OPTIONS: Option<InventoryTrackingMode>[] = [
  { value: 'DISABLED', label: 'settings:options.inventoryTrackingMode.disabled' },
  { value: 'TRACK_QUANTITY', label: 'settings:options.inventoryTrackingMode.trackQuantity' },
  { value: 'TRACK_BATCH', label: 'settings:options.inventoryTrackingMode.trackBatch' },
];

/** i18next keys — render with `t(option.label)`. */
export const RESERVATION_POLICY_OPTIONS: Option<ReservationPolicy>[] = [
  { value: 'NONE', label: 'settings:options.reservationPolicy.none' },
  { value: 'ON_CHECKOUT', label: 'settings:options.reservationPolicy.onCheckout' },
  { value: 'ON_PAYMENT', label: 'settings:options.reservationPolicy.onPayment' },
];

/** i18next keys — render with `t(option.label)`. */
export const OUT_OF_STOCK_BEHAVIOR_OPTIONS: Option<OutOfStockBehavior>[] = [
  { value: 'HIDE', label: 'settings:options.outOfStockBehavior.hide' },
  { value: 'SHOW_DISABLED', label: 'settings:options.outOfStockBehavior.showDisabled' },
  { value: 'SHOW_BACKORDER', label: 'settings:options.outOfStockBehavior.showBackorder' },
];

/** i18next keys — render with `t(option.label)`. */
export const CART_MERGE_STRATEGY_OPTIONS: Option<CartMergeStrategy>[] = [
  { value: 'MERGE', label: 'settings:options.cartMergeStrategy.merge' },
  { value: 'REPLACE', label: 'settings:options.cartMergeStrategy.replace' },
  { value: 'KEEP_GUEST', label: 'settings:options.cartMergeStrategy.keepGuest' },
];

/**
 * The 7 Posiflora sync capabilities exposed as MVP toggles on `ErpPosifloraSettingsPage` — a
 * considered subset of {@link PosifloraCapabilities}'s 11 keys. `customerExport`/
 * `customerImport`/`loyalty`/`purchases` are explicitly out of MVP scope and have no toggle; the
 * page still round-trips their stored value unchanged on every save (see the page's
 * `mergeCapabilities` helper) so it never silently resets a value the operator can't see.
 */
export type PosifloraCapabilityKey = keyof Pick<
  PosifloraCapabilities,
  'catalog' | 'inventory' | 'price' | 'store' | 'orderExport' | 'payment' | 'returns'
>;

/**
 * i18next keys — render with `t(option.label)`. Order matters: rendered in this order by
 * `MultiToggleField`, `catalog` first as this MVP's headline capability.
 */
export const POSIFLORA_CAPABILITY_OPTIONS: Option<PosifloraCapabilityKey>[] = [
  { value: 'catalog', label: 'settings:options.posifloraCapability.catalog' },
  { value: 'inventory', label: 'settings:options.posifloraCapability.inventory' },
  { value: 'price', label: 'settings:options.posifloraCapability.price' },
  { value: 'store', label: 'settings:options.posifloraCapability.store' },
  { value: 'orderExport', label: 'settings:options.posifloraCapability.orderExport' },
  { value: 'payment', label: 'settings:options.posifloraCapability.payment' },
  { value: 'returns', label: 'settings:options.posifloraCapability.returns' },
];

/**
 * The 10 known Evotor sync-pipeline step keys, in the BFF-documented display order. i18next keys
 * — render with `t(option.label)`. Order matters: `EvotorIntegrationSettingsPage` renders known
 * steps in this order, then any server-returned step whose key isn't in this list, appended after
 * (with a humanised fallback label — see `humanizeFlagKey`). `STOCK_EXPORT`/`PRICE_EXPORT` are the
 * automation-panel twins of the per-store `stockExportEnabled`/`priceExportEnabled` switches on
 * `RetailEvotorStoresPanel` (adr-integration-017) — this tenant-wide step is one of several gates
 * a store's own switch must also clear (see `RetailEvotorNotExportingReason`/
 * `RetailEvotorNotExportingPriceReason`'s `TENANT_PAUSED` value).
 */
export const EVOTOR_INTEGRATION_STEP_OPTIONS: Option<EvotorIntegrationStepKey>[] = [
  { value: 'PRODUCT_EXPORT', label: 'settings:options.evotorIntegrationStep.productExport' },
  {
    value: 'PRODUCT_GROUP_EXPORT',
    label: 'settings:options.evotorIntegrationStep.productGroupExport',
  },
  { value: 'STORE_SYNC', label: 'settings:options.evotorIntegrationStep.storeSync' },
  { value: 'EMPLOYEE_SYNC', label: 'settings:options.evotorIntegrationStep.employeeSync' },
  { value: 'DEVICE_SYNC', label: 'settings:options.evotorIntegrationStep.deviceSync' },
  { value: 'RECEIPT_INGEST', label: 'settings:options.evotorIntegrationStep.receiptIngest' },
  {
    value: 'RECEIPT_EVENT_PUBLISH',
    label: 'settings:options.evotorIntegrationStep.receiptEventPublish',
  },
  {
    value: 'CATALOG_PUBLICATION_INGEST',
    label: 'settings:options.evotorIntegrationStep.catalogPublicationIngest',
  },
  { value: 'STOCK_EXPORT', label: 'settings:options.evotorIntegrationStep.stockExport' },
  { value: 'PRICE_EXPORT', label: 'settings:options.evotorIntegrationStep.priceExport' },
];

// ── Tenant capabilities, sales-mode help & flag helpers ──────────────────────────

/** A well-known tenant storefront capability surfaced as a first-class toggle. */
export interface CapabilityFlag {
  /** The canonical tenant settings field key (mirrors web-tenant-settings-service FeatureFlags). */
  key: string;
  /** i18next key — render with `t(label)`. */
  label: string;
  /** i18next key — render with `t(description)`. */
  description: string;
}

/**
 * The platform storefront purchasing funnel, exposed as first-class capability toggles on the Tenant
 * settings page. Keys mirror web-tenant-settings-service `FeatureFlags`
 * (`catalogEnabled` … `paymentEnabled`). `label`/`description` are i18next keys — render with `t(...)`.
 */
export const TENANT_CAPABILITY_FLAGS: CapabilityFlag[] = [
  {
    key: 'catalogEnabled',
    label: 'settings:options.tenantCapabilityFlags.catalogEnabled.label',
    description: 'settings:options.tenantCapabilityFlags.catalogEnabled.description',
  },
  {
    key: 'basketEnabled',
    label: 'settings:options.tenantCapabilityFlags.basketEnabled.label',
    description: 'settings:options.tenantCapabilityFlags.basketEnabled.description',
  },
  {
    key: 'checkoutEnabled',
    label: 'settings:options.tenantCapabilityFlags.checkoutEnabled.label',
    description: 'settings:options.tenantCapabilityFlags.checkoutEnabled.description',
  },
  {
    key: 'ordersEnabled',
    label: 'settings:options.tenantCapabilityFlags.ordersEnabled.label',
    description: 'settings:options.tenantCapabilityFlags.ordersEnabled.description',
  },
  {
    key: 'paymentEnabled',
    label: 'settings:options.tenantCapabilityFlags.paymentEnabled.label',
    description: 'settings:options.tenantCapabilityFlags.paymentEnabled.description',
  },
];

/**
 * What each storefront sales mode does — shown as contextual help under the selector.
 * Values are i18next keys — render with `t(SALES_MODE_HELP[mode])`.
 */
export const SALES_MODE_HELP: Record<SalesMode, string> = {
  PRE_OPENING: 'settings:options.salesModeHelp.preOpening',
  ACTIVE: 'settings:options.salesModeHelp.active',
  PAUSED: 'settings:options.salesModeHelp.paused',
  MAINTENANCE: 'settings:options.salesModeHelp.maintenance',
};

/**
 * Day-of-week labels for working-hours editors (1 = Monday … 7 = Sunday). `label` is an
 * i18next key (shares `settings:options.weekday.*` with {@link STORE_DAY_OF_WEEK_OPTIONS} —
 * see that constant's doc comment) — render with `t(entry.label)`.
 */
export const WEEKDAYS: { day: number; label: string }[] = [
  { day: 1, label: 'settings:options.weekday.monday' },
  { day: 2, label: 'settings:options.weekday.tuesday' },
  { day: 3, label: 'settings:options.weekday.wednesday' },
  { day: 4, label: 'settings:options.weekday.thursday' },
  { day: 5, label: 'settings:options.weekday.friday' },
  { day: 6, label: 'settings:options.weekday.saturday' },
  { day: 7, label: 'settings:options.weekday.sunday' },
];

/**
 * Human-readable label for an arbitrary feature-flag key (e.g. `loyalty_program` → "Loyalty
 * program"). Fallback-only: used when a flag key has no entry in `TENANT_CAPABILITY_FLAGS`,
 * so there is no static key to translate — this always renders in English regardless of the
 * active UI language (best-effort humanisation of an arbitrary, unknown string). */
export function humanizeFlagKey(key: string): string {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\benabled\b/gi, '')
    .trim();
  if (!words) {
    return key;
  }
  return words.charAt(0).toUpperCase() + words.slice(1);
}
